// src/pages/championships/ChampionshipMatchesTab.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import championshipService from '../../services/championshipService';
import sponsorService from '../../services/sponsorService';
import useAuth from '../../hooks/useAuth';
import BannerGenerator from '../../components/BannerGenerator';
import './ChampionshipMatchesTab.css';

const formatDateTime = (dateString, type = 'full') => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return type === 'full' ? 'Data a definir' : '-';
    
    if (type === 'date') return date.toLocaleDateString('pt-BR');
    if (type === 'time') return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return 'Data inválida';
  }
};

const normalizeStatus = (status) => {
  if (!status) return 'scheduled';
  return status.toString().toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
};

const translatePhaseToPortuguese = (phase) => {
  const phases = {
    'eighthFinals': 'Oitavas de Final',
    'quarterFinals': 'Quartas de Final',
    'semiFinals': 'Semifinal',
    'thirdPlace': 'Disputa de 3º Lugar',
    'final': 'Final',
    'group': 'Fase de Grupos',
    'roundOf16': 'Oitavas de Final',
    'roundOf32': '32ªs de Final'
  };
  return phases[phase] || phase;
};

const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

function ChampionshipMatchesTab() {
  const { id: championshipId } = useParams();
  const { user, loadingAuth } = useAuth();

  const [matches, setMatches] = useState([]);
  const [championshipData, setChampionshipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sponsorsUrls, setSponsorsUrls] = useState([]);
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeGroupFilter, setActiveGroupFilter] = useState('all');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [isSavingMatch, setIsSavingMatch] = useState(false);

  // 🆕 Banner states
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const [selectedMatchForBanner, setSelectedMatchForBanner] = useState(null);
  const [selectedMatchesForBanner, setSelectedMatchesForBanner] = useState([]);
  const [selectedDateTitle, setSelectedDateTitle] = useState('');
  const [bannerMode, setBannerMode] = useState('match');
  
  // 🆕 Preview states
  const [showBannerPreview, setShowBannerPreview] = useState(false);
  const [bannerImageUrl, setBannerImageUrl] = useState(null);
  const [isGeneratingBanner, setIsGeneratingBanner] = useState(false);
  
  const bannerRef = useRef();

  const canManageMatches = useMemo(() => {
    if (loadingAuth || !user || !user.role || !championshipData) return false;
    if (['admin', 'gestor'].includes(user.role.toLowerCase())) {
        const userId = user._id;
        return (championshipData.creator?._id === userId || 
                championshipData.managers?.some(m => m._id === userId) || 
                user.role.toLowerCase() === 'admin');
    }
    return false;
  }, [user, loadingAuth, championshipData]);

  const loadData = useCallback(async () => {
    if (!championshipId) return;
    setLoading(true);
    try {
      const [matchesRes, champRes, sponsorsRes] = await Promise.all([
        championshipService.getMatchesByChampionship(championshipId),
        championshipService.getById(championshipId),
        sponsorService.listByChampionship(championshipId)
      ]);
      setMatches(Array.isArray(matchesRes.data) ? matchesRes.data : []);
      setChampionshipData(champRes.data);
      
      const rawSponsors = sponsorsRes.data || sponsorsRes || [];
      const urls = Array.isArray(rawSponsors)
        ? rawSponsors.map((s) => getImageUrl(s.image)).filter(Boolean)
        : [];
      setSponsorsUrls(urls);
    } catch (err) {
      setError('Erro ao carregar dados das partidas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => { if (!loadingAuth) loadData(); }, [loadData, loadingAuth]);

  // ✅ FUNÇÃO DE IMPRESSÃO
  const handlePrintMatches = (title, matchesToPrint) => {
    const champName = championshipData?.name.toUpperCase() || 'CAMPEONATO';
    const logoUrl = championshipData?.logo ? getImageUrl(championshipData.logo) : null;

    const sorted = [...matchesToPrint].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
    const grouped = sorted.reduce((acc, match) => {
      const d = formatDateTime(match.date, 'date');
      if (!acc[d]) acc[d] = [];
      acc[d].push(match);
      return acc;
    }, {});

    let tablesHtml = '';

    Object.entries(grouped).forEach(([date, games]) => {
      tablesHtml += `
        <div class="print-section">
          <table class="report-table">
            <thead>
              <tr class="report-header-main">
                <th colspan="5">TABELA DE JOGOS - ${date}</th>
              </tr>
              <tr class="report-header-subs">
                <th style="width: 60px">HORA</th>
                <th style="text-align: right; width: 40%">EQUIPE A</th>
                <th style="width: 30px">X</th>
                <th style="text-align: left; width: 40%">EQUIPE B</th>
                <th>REF/GRP</th>
              </tr>
            </thead>
            <tbody>
              ${games.map(m => `
                <tr>
                  <td class="bold">${formatDateTime(m.date, 'time')}</td>
                  <td style="text-align: right">${(m.teamA?.name || 'A DEFINIR').toUpperCase()}</td>
                  <td class="bold bg-light">${m.scoreA ?? ''} x ${m.scoreB ?? ''}</td>
                  <td style="text-align: left">${(m.teamB?.name || 'A DEFINIR').toUpperCase()}</td>
                  <td style="font-size: 8pt">${m.group ? ' ' + m.group : translatePhaseToPortuguese(m.phase)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Partidas - ${champName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
            body { font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 30px; color: #333; }
            .header-container { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #008080; padding-bottom: 10px; }
            .logo { max-height: 70px; margin-bottom: 5px; }
            .champ-title { font-size: 18pt; margin: 0; color: #000; font-weight: bold; }
            .report-title { font-size: 14pt; color: #555; margin-bottom: 15px; }
            .print-section { page-break-inside: avoid; margin-bottom: 30px; }
            .report-table { width: 100%; border-collapse: collapse; border: 1px solid #000; }
            .report-table th, .report-table td { border: 1px solid #000; padding: 8px 5px; text-align: center; font-size: 10pt; }
            .report-header-main { background-color: #008080 !important; color: white !important; -webkit-print-color-adjust: exact; }
            .report-header-subs { background-color: #f2f2f2 !important; font-weight: bold; -webkit-print-color-adjust: exact; }
            .bold { font-weight: bold; }
            .bg-light { background-color: #f9f9f9; -webkit-print-color-adjust: exact; }
            @media print { .no-print { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="no-print" style="text-align: center; margin-bottom: 20px;">
            <button onclick="window.print()" style="padding: 10px 20px; cursor: pointer;">Imprimir Tabela</button>
          </div>
          <div class="header-container">
            ${logoUrl ? `<img src="${logoUrl}" class="logo" />` : ''}
            <h1 class="champ-title">${champName}</h1>
            <div class="report-title">${title.toUpperCase()}</div>
          </div>
          ${tablesHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // 🔥 FUNÇÃO PARA GERAR BANNER DE PARTIDA INDIVIDUAL
  const generateMatchBanner = async (match) => {
    const matchWithLocation = {
      ...match,
      location: match.location || 'LOCAL A DEFINIR'
    };
    
    setSelectedMatchForBanner(matchWithLocation);
    setBannerMode('match');
    setIsGeneratingBanner(true);
    
    setTimeout(async () => {
      if (bannerRef.current) {
        try {
          const dataUrl = await bannerRef.current.generateBanner();
          setBannerImageUrl(dataUrl);
          setShowBannerPreview(true);
        } catch (error) {
          console.error('Erro ao gerar banner:', error);
          alert('Erro ao gerar banner. Tente novamente.');
        } finally {
          setIsGeneratingBanner(false);
        }
      }
    }, 500);
  };

  const getFormattedDateRange = (matchesList) => {
    if (!matchesList || matchesList.length === 0) return '';
    const dates = matchesList
      .map(m => m.date ? new Date(m.date) : null)
      .filter(d => d && !isNaN(d.getTime()));
    if (dates.length === 0) return '';
    dates.sort((a, b) => a - b);
    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];
    const formatDate = (date) => date.toLocaleDateString('pt-BR');
    if (firstDate.toDateString() === lastDate.toDateString()) {
      return formatDate(firstDate);
    }
    return `${formatDate(firstDate)} a ${formatDate(lastDate)}`;
  };

  // 🔥 FUNÇÃO PARA GERAR BANNER DE RODADA COM SUPORTE A CHUNKS
  const generateRoundBanner = async (titlePrefix, matchesList, chunkIndex = 0, totalChunks = 1) => {
    // Divide os jogos em grupos de 4
    const chunkSize = 4;
    const chunks = [];
    for (let i = 0; i < matchesList.length; i += chunkSize) {
      chunks.push(matchesList.slice(i, i + chunkSize));
    }

    // Se não for especificado um chunk específico, usa o primeiro
    const targetChunk = chunks[chunkIndex] || chunks[0];
    const startGame = chunkIndex * chunkSize + 1;
    const endGame = Math.min(startGame + targetChunk.length - 1, matchesList.length);
    
    // Título com intervalo dos jogos
    const dateRange = getFormattedDateRange(targetChunk);
    let title = dateRange ? `${titlePrefix} - ${dateRange}` : titlePrefix;
    
    // Se tem mais de um chunk, adiciona o intervalo de jogos
    if (totalChunks > 1 || chunks.length > 1) {
      title = `${titlePrefix} (Jogos ${startGame}-${endGame})`;
    }
    
    // 🔥 CORREÇÃO: Garante que os matches tenham location
    const matchesWithLocation = targetChunk.map(m => ({
      ...m,
      location: m.location || 'LOCAL A DEFINIR'
    }));
    
    setSelectedDateTitle(title);
    setSelectedMatchesForBanner(matchesWithLocation);
    setBannerMode('round');
    setIsGeneratingBanner(true);
    
    setTimeout(async () => {
      if (bannerRef.current) {
        try {
          const dataUrl = await bannerRef.current.generateBanner();
          setBannerImageUrl(dataUrl);
          setShowBannerPreview(true);
        } catch (error) {
          console.error('Erro ao gerar banner:', error);
          alert('Erro ao gerar banner. Tente novamente.');
        } finally {
          setIsGeneratingBanner(false);
        }
      }
    }, 500);
  };

  // 🔥 FUNÇÃO PARA GERAR BANNER DO DIA
  const generateDayBanner = async () => {
    const today = new Date();
    const todayMatches = matches.filter(match => {
      if (!match.date) return false;
      const matchDate = new Date(match.date);
      return matchDate.toDateString() === today.toDateString();
    });
    
    if (todayMatches.length === 0) {
      alert('Não há jogos programados para hoje.');
      return;
    }
    
    const matchesWithLocation = todayMatches.map(m => ({
      ...m,
      location: m.location || 'LOCAL A DEFINIR'
    }));
    
    setSelectedDateTitle('JOGOS DE HOJE');
    setSelectedMatchesForBanner(matchesWithLocation);
    setBannerMode('day');
    setIsGeneratingBanner(true);
    
    setTimeout(async () => {
      if (bannerRef.current) {
        try {
          const dataUrl = await bannerRef.current.generateBanner();
          setBannerImageUrl(dataUrl);
          setShowBannerPreview(true);
        } catch (error) {
          console.error('Erro ao gerar banner:', error);
          alert('Erro ao gerar banner. Tente novamente.');
        } finally {
          setIsGeneratingBanner(false);
        }
      }
    }, 500);
  };

  // 🆕 Função para baixar o banner
  const downloadBanner = () => {
    if (bannerImageUrl) {
      const link = document.createElement('a');
      link.download = `banner-${Date.now()}.png`;
      link.href = bannerImageUrl;
      link.click();
    }
  };

  const filteredMatchesList = useMemo(() => {
    return matches.filter(match => {
      const statusPassed = activeFilter === 'all' || (activeFilter === 'scheduled' && (!match.status || match.status === 'scheduled')) || match.status === activeFilter;
      const groupPassed = !championshipData?.format?.toLowerCase().includes('grupo') || activeGroupFilter === 'all' || match.group === activeGroupFilter;
      return statusPassed && groupPassed;
    });
  }, [matches, activeFilter, activeGroupFilter, championshipData]);

  const groupedMatches = useMemo(() => {
    return filteredMatchesList.reduce((acc, match) => {
      const key = match.phase === 'group' ? 'group' : match.phase;
      if (!acc[key]) acc[key] = [];
      acc[key].push(match);
      return acc;
    }, {});
  }, [filteredMatchesList]);

  const sortedPhases = useMemo(() => {
    const knockoutOrder = ['roundOf32', 'roundOf16', 'quarterFinals', 'semiFinals', 'thirdPlace', 'final'];
    return Object.keys(groupedMatches).sort((a, b) => {
      if (a === 'group') return -1;
      if (b === 'group') return 1;
      return knockoutOrder.indexOf(a) - knockoutOrder.indexOf(b);
    });
  }, [groupedMatches]);

  const handleEditMatchClick = (match) => {
    if (!canManageMatches) return;
    const matchDate = match.date ? new Date(match.date) : new Date();
    setEditingMatch({
      ...match,
      scoreA: match.scoreA ?? '', 
      scoreB: match.scoreB ?? '', 
      date: match.date ? match.date.split('T')[0] : '',
      time: match.date ? matchDate.toTimeString().substring(0, 5) : '21:00',
      location: match.location || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSaveModalMatch = async () => {
    if (!editingMatch || !canManageMatches) return;
    setIsSavingMatch(true);
    try {
      const dateTime = editingMatch.date ? new Date(`${editingMatch.date}T${editingMatch.time || '12:00'}:00`) : null;
      await championshipService.updateMatch(editingMatch._id, {
        date: dateTime ? dateTime.toISOString() : null,
        scoreA: editingMatch.scoreA === '' ? null : editingMatch.scoreA,
        scoreB: editingMatch.scoreB === '' ? null : editingMatch.scoreB,
        status: editingMatch.status,
        location: editingMatch.location || 'A Definir',
      });
      await loadData();
      setIsEditModalOpen(false);
    } catch (error) {
      alert('Erro ao salvar');
    } finally {
      setIsSavingMatch(false);
    }
  };

  if (loadingAuth || loading) return <p className="status-message">Carregando...</p>;

  return (
    <div className="matches-section">
      <div className="matches-header">
        <h2 className="section-title">Partidas do Campeonato</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={generateDayBanner}
            className="print-button-matches"
            style={{ backgroundColor: '#f97316' }}
            disabled={isGeneratingBanner}
          >
            {isGeneratingBanner ? '⏳ Gerando...' : '📸 Banner do Dia'}
          </button>
          <button 
            onClick={() => handlePrintMatches("Calendário Completo", filteredMatchesList)}
            className="print-button-matches"
          >
            📄 Imprimir Calendário
          </button>
        </div>
      </div>

      {/* 🆕 SELETOR DE FUNDO DO BANNER */}
      <div className="banner-bg-selector" style={{
        backgroundColor: 'rgba(0,0,0,0.6)',
        borderRadius: 12,
        padding: 10,
        marginBottom: 15,
        display: 'flex',
        alignItems: 'center',
        gap: 15,
        flexWrap: 'wrap',
      }}>
        <span style={{ color: '#fff', fontWeight: 600 }}>Fundo do banner:</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map((idx) => (
            <button
              key={idx}
              onClick={() => setBackgroundIndex(idx)}
              style={{
                padding: '6px 16px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: backgroundIndex === idx ? '#f97316' : '#374151',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Fundo {idx + 1}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-bar">
        {['all', 'scheduled', 'in-progress', 'completed'].map(f => (
          <button key={f} onClick={() => setActiveFilter(f)} className={activeFilter === f ? 'active' : ''}>
            {f === 'all' ? 'Todos' : f === 'scheduled' ? 'Próximos' : f === 'in-progress' ? 'Em Andamento' : 'Finalizados'}
          </button>
        ))}
      </div>

      <div className="rounds-container">
        {sortedPhases.map((phaseKey) => {
          if (phaseKey === 'group') {
            const rounds = groupedMatches.group.reduce((acc, m) => {
              const r = m.round || '1';
              if (!acc[r]) acc[r] = [];
              acc[r].push(m);
              return acc;
            }, {});

            return Object.keys(rounds).sort((a,b) => a-b).map(rNum => {
              const roundMatches = rounds[rNum];
              const chunkSize = 4;
              const chunks = [];
              for (let i = 0; i < roundMatches.length; i += chunkSize) {
                chunks.push(roundMatches.slice(i, i + chunkSize));
              }
              const totalChunks = chunks.length;

              return (
                <div key={`r-${rNum}`} className="round-section">
                  <div className="round-title-container">
                    <h3 className="round-title">Fase de Grupos - Rodada {rNum}</h3>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {/* 🔥 BOTÕES DE BANNER POR CHUNK */}
                      {chunks.map((chunk, idx) => {
                        const startGame = idx * chunkSize + 1;
                        const endGame = Math.min(startGame + chunk.length - 1, roundMatches.length);
                        return (
                          <button 
                            key={`banner-${rNum}-${idx}`}
                            onClick={() => generateRoundBanner(`Rodada ${rNum}`, roundMatches, idx, totalChunks)}
                            className="btn-pdf"
                            style={{ backgroundColor: '#f97316' }}
                            disabled={isGeneratingBanner}
                          >
                            📸 {totalChunks > 1 ? `Jogos ${startGame}-${endGame}` : 'Banner'}
                          </button>
                        );
                      })}
                      <button 
                        onClick={() => handlePrintMatches(`Rodada ${rNum}`, roundMatches)} 
                        className="btn-pdf"
                      >
                        📄 Salvar Rodada
                      </button>
                    </div>
                  </div>
                  <div className="matches-grid">
                    {roundMatches.map(m => (
                      <div key={m._id} className={`match-card ${normalizeStatus(m.status)}`}>
                        <div className="match-card-header"><span className="match-date">{formatDateTime(m.date, 'full')}</span></div>
                        <div className="match-card-body">
                          <div className="team-info home-team"><span>{m.teamA?.name || 'A Definir'}</span></div>
                          <div className="match-score"><span>{m.scoreA ?? '-'}</span><span>x</span><span>{m.scoreB ?? '-'}</span></div>
                          <div className="team-info away-team"><span>{m.teamB?.name || 'A Definir'}</span></div>
                        </div>
                        <div className="match-card-footer">
                          {canManageMatches && (
                            <button onClick={() => handleEditMatchClick(m)} className="action-button edit-button">
                              Editar
                            </button>
                          )}
                          <button 
                            onClick={() => generateMatchBanner(m)}
                            className="action-button"
                            style={{ backgroundColor: '#f97316', color: '#fff' }}
                            disabled={isGeneratingBanner}
                          >
                            Banner
                          </button>
                          <Link to={`/championships/${championshipId}/matches/${m._id}`} className="action-button details-button">
                            Detalhes
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          } else {
            const title = translatePhaseToPortuguese(phaseKey);
            const phaseMatches = groupedMatches[phaseKey];
            const chunkSize = 4;
            const chunks = [];
            for (let i = 0; i < phaseMatches.length; i += chunkSize) {
              chunks.push(phaseMatches.slice(i, i + chunkSize));
            }
            const totalChunks = chunks.length;

            return (
              <div key={phaseKey} className="round-section">
                <div className="round-title-container">
                  <h3 className="round-title">{title}</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* 🔥 BOTÕES DE BANNER POR CHUNK */}
                    {chunks.map((chunk, idx) => {
                      const startGame = idx * chunkSize + 1;
                      const endGame = Math.min(startGame + chunk.length - 1, phaseMatches.length);
                      return (
                        <button 
                          key={`banner-${phaseKey}-${idx}`}
                          onClick={() => generateRoundBanner(title, phaseMatches, idx, totalChunks)}
                          className="btn-pdf"
                          style={{ backgroundColor: '#f97316' }}
                          disabled={isGeneratingBanner}
                        >
                          📸 {totalChunks > 1 ? `Jogos ${startGame}-${endGame}` : 'Banner'}
                        </button>
                      );
                    })}
                    <button 
                      onClick={() => handlePrintMatches(title, phaseMatches)} 
                      className="btn-pdf"
                    >
                      📄 Salvar Fase
                    </button>
                  </div>
                </div>
                <div className="matches-grid">
                  {phaseMatches.map(m => (
                    <div key={m._id} className={`match-card ${normalizeStatus(m.status)}`}>
                      <div className="match-card-header"><span className="match-date">{formatDateTime(m.date, 'full')}</span></div>
                      <div className="match-card-body">
                        <div className="team-info home-team"><span>{m.teamA?.name || 'A Definir'}</span></div>
                        <div className="match-score"><span>{m.scoreA ?? '-'}</span><span>x</span><span>{m.scoreB ?? '-'}</span></div>
                        <div className="team-info away-team"><span>{m.teamB?.name || 'A Definir'}</span></div>
                      </div>
                      <div className="match-card-footer">
                        {canManageMatches && (
                          <button onClick={() => handleEditMatchClick(m)} className="action-button edit-button">
                            Editar
                          </button>
                        )}
                        <button 
                          onClick={() => generateMatchBanner(m)}
                          className="action-button"
                          style={{ backgroundColor: '#f97316', color: '#fff' }}
                          disabled={isGeneratingBanner}
                        >
                          Banner
                        </button>
                        <Link to={`/championships/${championshipId}/matches/${m._id}`} className="action-button details-button">
                          Detalhes
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        })}
      </div>

      {/* 🆕 BANNER GENERATOR OCULTO */}
      <BannerGenerator
        ref={bannerRef}
        mode={bannerMode}
        championship={championshipData}
        match={selectedMatchForBanner}
        matches={selectedMatchesForBanner}
        dateTitle={selectedDateTitle}
        backgroundIndex={backgroundIndex}
        sponsors={sponsorsUrls}
      />

      {/* 🆕 MODAL DE PRÉ-VISUALIZAÇÃO DO BANNER */}
      {showBannerPreview && bannerImageUrl && (
        <div 
          className="banner-preview-overlay"
          onClick={() => {
            setShowBannerPreview(false);
            setBannerImageUrl(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div 
            className="banner-preview-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '95vw',
              maxHeight: '95vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px',
              color: '#fff',
            }}>
              <h3 style={{ margin: 0, fontSize: '20px' }}>📸 Pré-visualização do Banner</h3>
              <button
                onClick={() => {
                  setShowBannerPreview(false);
                  setBannerImageUrl(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{
              backgroundColor: '#0d1117',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <img 
                src={bannerImageUrl} 
                alt="Banner Preview"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={downloadBanner}
                style={{
                  padding: '12px 40px',
                  backgroundColor: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
              >
                📥 Baixar Banner
              </button>
              <button
                onClick={() => {
                  setShowBannerPreview(false);
                  setBannerImageUrl(null);
                }}
                style={{
                  padding: '12px 40px',
                  backgroundColor: '#374151',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#374151'}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editingMatch && (
        <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Editar Partida</h3>
            <div className="form-group"><label>Data:</label><input type="date" name="date" value={editingMatch.date} onChange={e => setEditingMatch({...editingMatch, date: e.target.value})} /></div>
            <div className="form-group"><label>Hora:</label><input type="time" name="time" value={editingMatch.time} onChange={e => setEditingMatch({...editingMatch, time: e.target.value})} /></div>
            <div className="form-group"><label>Local:</label><input type="text" value={editingMatch.location} onChange={e => setEditingMatch({...editingMatch, location: e.target.value})} /></div>
            <div className="form-group"><label>Placar A:</label><input type="number" value={editingMatch.scoreA} onChange={e => setEditingMatch({...editingMatch, scoreA: e.target.value})} /></div>
            <div className="form-group"><label>Placar B:</label><input type="number" value={editingMatch.scoreB} onChange={e => setEditingMatch({...editingMatch, scoreB: e.target.value})} /></div>
            <div className="form-group">
              <label>Status:</label>
              <select value={editingMatch.status} onChange={e => setEditingMatch({...editingMatch, status: e.target.value})}>
                <option value="scheduled">Agendada</option><option value="in-progress">Em Andamento</option><option value="completed">Concluída</option>
              </select>
            </div>
            <div className="modal-actions">
              <button onClick={() => setIsEditModalOpen(false)} className="cancel-button">Cancelar</button>
              <button onClick={handleSaveModalMatch} disabled={isSavingMatch} className="save-button">{isSavingMatch ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChampionshipMatchesTab;